import React from 'react'
import ImageUploader from '../ImageUploader'
import TextInput from '../inputs/TextInput'

interface ImagePropertyFieldProps {
	value: any
	onChange: (value: any) => void
	placeholder?: string
}

export default function ImagePropertyField({ value, onChange, placeholder }: ImagePropertyFieldProps) {
	// Estrutura: { desktop: { value: "...", type: "image" }, mobile: { value: "...", type: "image" }, link: { value: "..." }, alt_text: { value: "..." } }
	const desktopValue = value?.desktop?.value || ''
	const mobileValue = value?.mobile?.value || ''
	const linkValue = value?.link?.value || ''
	const altTextValue = value?.alt_text?.value || ''
	const desktopType = value?.desktop?.type || 'image'
	const mobileType = value?.mobile?.type || 'image'

	const [desktop, setDesktop] = React.useState(desktopValue)
	const [mobile, setMobile] = React.useState(mobileValue)
	const [link, setLink] = React.useState(linkValue)
	const [altText, setAltText] = React.useState(altTextValue)
	const [desktopTypeState, setDesktopTypeState] = React.useState(desktopType)
	const [mobileTypeState, setMobileTypeState] = React.useState(mobileType)
	const [validationError, setValidationError] = React.useState<string>('')

	// Função para gerar URL da thumbnail a partir da URL do vídeo do Supabase
	const getSupabaseThumbnailUrl = (videoUrl: string): string | undefined => {
		if (!videoUrl || !videoUrl.includes('.supabase.co/storage/v1/object/public/')) {
			return undefined;
		}

		try {
			// Substituir '/videos/' por '/thumbnails/'
			let thumbnailUrl = videoUrl.replace('/videos/', '/thumbnails/');
			
			// Remover a extensão do vídeo e adicionar '_thumb.jpg'
			thumbnailUrl = thumbnailUrl.replace(/\.[^/.]+$/, '_thumb.jpg');
			
			return thumbnailUrl;
		} catch (error) {
			console.warn('Erro ao derivar URL da thumbnail:', error);
			return undefined;
		}
	}

	React.useEffect(() => {
		setDesktop(value?.desktop?.value || '')
		setMobile(value?.mobile?.value || '')
		setLink(value?.link?.value || '')
		setAltText(value?.alt_text?.value || '')
		setDesktopTypeState(value?.desktop?.type || 'image')
		setMobileTypeState(value?.mobile?.type || 'image')
	}, [value])

	const updateValue = (updates: any) => {
		console.log('🔄 ImagePropertyField updateValue:', { updates, desktop, mobile, desktopTypeState, mobileTypeState })

		// Validação: desktop e mobile devem ter o mesmo tipo (image/video)
		const newDesktopType = updates.desktopType !== undefined ? updates.desktopType : desktopTypeState
		const newMobileType = updates.mobileType !== undefined ? updates.mobileType : mobileTypeState

		// Verificar se há conflito de tipos
		const hasDesktopValue = updates.desktop !== undefined ? updates.desktop : desktop
		const hasMobileValue = updates.mobile !== undefined ? updates.mobile : mobile

		// Se ambos têm valores definidos, verificar se os tipos são compatíveis
		if (hasDesktopValue && hasMobileValue && newDesktopType !== newMobileType) {
			const errorMessage = `Não é possível misturar tipos diferentes. Desktop deve ser ${newMobileType === 'video' ? 'vídeo' : 'imagem'} para ser compatível com mobile.`
			setValidationError(errorMessage)
			console.warn('🚫 Validação falhou:', errorMessage)
			return
		}

		// Se estou definindo desktop e mobile já tem valor, verificar compatibilidade
		if (updates.desktopType !== undefined && hasMobileValue && newDesktopType !== mobileTypeState) {
			const errorMessage = `Desktop deve ser ${mobileTypeState === 'video' ? 'vídeo' : 'imagem'} para ser compatível com mobile.`
			setValidationError(errorMessage)
			console.warn('🚫 Validação falhou:', errorMessage)
			return
		}

		// Se estou definindo mobile e desktop já tem valor, verificar compatibilidade
		if (updates.mobileType !== undefined && hasDesktopValue && newMobileType !== desktopTypeState) {
			const errorMessage = `Mobile deve ser ${desktopTypeState === 'video' ? 'vídeo' : 'imagem'} para ser compatível com desktop.`
			setValidationError(errorMessage)
			console.warn('🚫 Validação falhou:', errorMessage)
			return
		}

		// Limpar erro de validação se chegou até aqui
		setValidationError('')

		const newValue = {
			desktop: {
				value: updates.desktop !== undefined ? updates.desktop : desktop,
				type: newDesktopType
			},
			mobile: {
				value: updates.mobile !== undefined ? updates.mobile : mobile,
				type: newMobileType
			},
			link: { value: updates.link !== undefined ? updates.link : link },
			alt_text: { value: updates.alt_text !== undefined ? updates.alt_text : altText }
		}

		console.log('📝 ImagePropertyField newValue:', newValue)
		onChange(newValue)
	}

	return (
		<div className="space-y-3">
			{/* Mensagem de erro de validação */}
			{validationError && (
				<div className="p-3 bg-red-50 border border-red-200 rounded-md">
					<div className="flex items-center space-x-2">
						<div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
							<svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
								<path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
							</svg>
						</div>
						<div>
							<p className="text-sm font-medium text-red-800">Tipo de mídia incompatível</p>
							<p className="text-xs text-red-600">{validationError}</p>
						</div>
					</div>
				</div>
			)}

			<div>
				<label className="block text-xs font-medium text-gray-700 mb-1">
					{desktopTypeState === 'video' ? 'Vídeo Desktop' : 'Imagem Desktop'}
				</label>
				<ImageUploader
					currentImage={desktop}
					currentThumbnailUrl={desktopTypeState === 'video' ? getSupabaseThumbnailUrl(desktop) : undefined}
					onImageSelect={(url, type) => updateValue({ desktop: url, desktopType: type })}
					type="desktop"
					pairedType={mobileTypeState}
					pairedHasValue={!!mobile}
				/>
			</div>

			<div>
				<label className="block text-xs font-medium text-gray-700 mb-1">
					{mobileTypeState === 'video' ? 'Vídeo Mobile' : 'Imagem Mobile'}
				</label>
				<ImageUploader
					currentImage={mobile}
					currentThumbnailUrl={mobileTypeState === 'video' ? getSupabaseThumbnailUrl(mobile) : undefined}
					onImageSelect={(url, type) => updateValue({ mobile: url, mobileType: type })}
					type="mobile"
					pairedType={desktopTypeState}
					pairedHasValue={!!desktop}
				/>
			</div>

			<div>
				<label className="block text-xs font-medium text-gray-700 mb-1">Link do Banner</label>
				<TextInput 
					type="url" 
					value={link} 
					onChange={(v) => updateValue({ link: v })} 
					placeholder="URL do link (opcional)" 
				/>
			</div>

			<div>
				<label className="block text-xs font-medium text-gray-700 mb-1">Alt Text</label>
				<TextInput 
					type="text" 
					value={altText} 
					onChange={(v) => updateValue({ alt_text: v })} 
					placeholder="Texto alternativo para acessibilidade (opcional)" 
				/>
			</div>
		</div>
	)
}


